import { useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'

import http from '../../../../shared/http'
import { TApi } from '../../../../shared/const'
import { askConfirm } from '../../../../shared/confirmDialog'

import Btn from '../../../../UI/Btn'

import styles from './style.module.scss'

const MIN_ANSWERS = 2
const MAX_ANSWERS = 6

/**
 * Prüft eine Frage vor dem Speichern. Liefert einen Satz oder null.
 *
 * Bei Einfachauswahl genau eine richtige Antwort: Zwei richtige bei einem
 * Kreis zum Ankreuzen ließen sich nie beide treffen, die Frage wäre
 * unlösbar.
 */
export const questionError = (frage, t = text => text) => {
	if (!frage.text.trim()) return t('Please enter the question.')
	const antworten = frage.answers.filter(antwort => antwort.text.trim())
	if (antworten.length < MIN_ANSWERS) return t('At least two answers are needed.')
	const richtige = antworten.filter(antwort => antwort.correct).length
	if (richtige === 0) return t('Mark at least one answer as correct.')
	if (!frage.multiple && richtige > 1) return t('With single choice exactly one answer is correct.')
	if (frage.multiple && richtige === antworten.length) return t('Not all answers can be correct.')
	return null
}

const leereFrage = () => ({
	text: '',
	multiple: false,
	explanation: '',
	reviewed: false,
	answers: [
		{ text: '', correct: true },
		{ text: '', correct: false },
		{ text: '', correct: false }
	]
})

const ausFrage = frage => ({
	text: frage.text || '',
	multiple: Boolean(frage.multiple),
	explanation: frage.explanation || '',
	reviewed: Boolean(frage.reviewedAt || frage.reviewed),
	answers: (frage.answers || []).map(antwort => ({
		id: antwort.id,
		text: antwort.text || '',
		correct: Boolean(antwort.correct)
	}))
})

/**
 * Der Fragenpool je Modul.
 *
 * Gezogen werden im Quiz so viele Fragen, wie der Kurs vorgibt; liegen mehr im
 * Pool, bekommt nicht jede:r dieselben — Abschreiben lohnt sich weniger.
 * Fragen, die Junior Achievement noch nicht abgenommen hat, sind orange
 * markiert; sie zählen trotzdem, sonst gäbe es am Anfang gar kein Quiz.
 */
export default function QuestionsTab({ course, onChanged, markDirty }) {
	const { t } = useTranslation()
	const proQuiz = course.questionsPerQuiz || 5

	return (
		<div>
			{(course.modules || []).length === 0 && (
				<p className={styles.intro}>{t('Create modules first — questions belong to a module.')}</p>
			)}
			{(course.modules || []).map((modul, index) => (
				<ModuleQuestions
					key={modul.id}
					modul={modul}
					index={index}
					proQuiz={proQuiz}
					onChanged={onChanged}
					markDirty={markDirty}
				/>
			))}
		</div>
	)
}

QuestionsTab.propTypes = {
	course: PropTypes.object.isRequired,
	onChanged: PropTypes.func.isRequired,
	markDirty: PropTypes.func.isRequired
}

function ModuleQuestions({ modul, index, proQuiz, onChanged, markDirty }) {
	const { t } = useTranslation()
	const [neu, setNeu] = useState(null)
	const fragen = modul.questions || []
	const offen = fragen.filter(frage => !frage.reviewedAt && !frage.reviewed).length

	return (
		<section className={styles.module}>
			<div className={styles.moduleHead}>
				<span className={styles.moduleNo}>{t('Module {{n}}', { n: index + 1 })}</span>
				<span className={styles.lessonName} style={{ flex: 1 }}>
					{modul.title}
				</span>
				<span className={styles.lessonMeta}>
					{t('{{n}} questions in the pool, {{m}} per quiz', { n: fragen.length, m: proQuiz })}
					{offen > 0 && ` · ${t('{{n}} not reviewed', { n: offen })}`}
				</span>
			</div>
			<div className={styles.moduleBody}>
				{fragen.length < proQuiz && (
					<p className={styles.warn}>
						{t('Fewer questions than per quiz ({{n}}) — the quiz of this module cannot start yet.', {
							n: proQuiz
						})}
					</p>
				)}

				{fragen.map((frage, nr) => (
					<QuestionEditor
						key={frage.id}
						frage={frage}
						nr={nr + 1}
						moduleId={modul.id}
						onChanged={onChanged}
						markDirty={markDirty}
					/>
				))}

				{neu && (
					<QuestionEditor
						frage={null}
						nr={fragen.length + 1}
						moduleId={modul.id}
						onChanged={() => {
							setNeu(null)
							onChanged()
						}}
						onCancel={() => setNeu(null)}
						markDirty={markDirty}
					/>
				)}

				{!neu && (
					<Btn theme="secondary" onClick={() => setNeu(true)}>
						{t('Add question')}
					</Btn>
				)}
			</div>
		</section>
	)
}

ModuleQuestions.propTypes = {
	modul: PropTypes.object.isRequired,
	index: PropTypes.number.isRequired,
	proQuiz: PropTypes.number.isRequired,
	onChanged: PropTypes.func.isRequired,
	markDirty: PropTypes.func.isRequired
}

export function QuestionEditor({ frage, nr, moduleId, onChanged, onCancel, markDirty }) {
	const { t } = useTranslation()
	const ausgang = useMemo(() => (frage ? ausFrage(frage) : leereFrage()), [frage])
	const [werte, setWerte] = useState(ausgang)
	const [speichert, setSpeichert] = useState(false)
	const schluessel = frage ? `question-${frage.id}` : `question-new-${moduleId}`

	useEffect(() => setWerte(ausgang), [ausgang])

	const geaendert = !frage || JSON.stringify(werte) !== JSON.stringify(ausgang)
	useEffect(() => markDirty(schluessel, geaendert), [geaendert, markDirty, schluessel])
	useEffect(() => () => markDirty(schluessel, false), [markDirty, schluessel])

	const setze = (feld, wert) => setWerte(vorher => ({ ...vorher, [feld]: wert }))
	const setzeAntwort = (index, feld, wert) =>
		setWerte(vorher => ({
			...vorher,
			answers: vorher.answers.map((antwort, i) => {
				if (i === index) return { ...antwort, [feld]: wert }
				// Einfachauswahl: eine neue richtige Antwort nimmt der alten den Haken.
				if (feld === 'correct' && wert && !vorher.multiple) return { ...antwort, correct: false }
				return antwort
			})
		}))

	const speichern = () => {
		const problem = questionError(werte, t)
		if (problem) return toast.error(problem)

		const rumpf = {
			text: werte.text.trim(),
			multiple: werte.multiple,
			explanation: werte.explanation.trim(),
			reviewed: werte.reviewed,
			answers: werte.answers
				.filter(antwort => antwort.text.trim())
				.map(antwort => ({ ...(antwort.id ? { id: antwort.id } : {}), text: antwort.text.trim(), correct: antwort.correct }))
		}

		setSpeichert(true)
		const anfrage = frage
			? http.put(TApi.ADMIN_COURSE_QUESTION(frage.id), rumpf)
			: http.post(TApi.ADMIN_COURSE_QUESTIONS(moduleId), rumpf)
		anfrage
			.then(() => {
				toast.success(t('Saved'))
				onChanged()
			})
			.finally(() => setSpeichert(false))
		return undefined
	}

	const loeschen = async () => {
		if (!frage) return onCancel && onCancel()
		const ok = await askConfirm({
			title: t('Delete question?'),
			confirmLabel: t('Delete'),
			tone: 'danger'
		})
		if (ok) http.delete(TApi.ADMIN_COURSE_QUESTION(frage.id)).then(() => onChanged())
		return undefined
	}

	return (
		<div className={clsx(styles.question, werte.reviewed ? styles.questionReviewed : styles.questionDraft)}>
			<label className={styles.field}>
				{t('Question {{n}}', { n: nr })}
				<textarea rows={2} value={werte.text} onChange={e => setze('text', e.target.value)} />
			</label>

			<div className={styles.checks}>
				<label>
					<input
						type="checkbox"
						checked={werte.multiple}
						onChange={e => setze('multiple', e.target.checked)}
					/>
					{t('Several answers can be correct')}
				</label>
				<label>
					<input
						type="checkbox"
						checked={werte.reviewed}
						onChange={e => setze('reviewed', e.target.checked)}
					/>
					{t('Reviewed by Junior Achievement')}
				</label>
			</div>

			{werte.answers.map((antwort, index) => (
				<div key={antwort.id || `neu-${index}`} className={styles.answerRow}>
					<label className={styles.correctLabel}>
						<input
							type={werte.multiple ? 'checkbox' : 'radio'}
							name={`richtig-${schluessel}`}
							checked={antwort.correct}
							onChange={e => setzeAntwort(index, 'correct', e.target.checked)}
						/>
						{t('correct')}
					</label>
					<input
						type="text"
						value={antwort.text}
						placeholder={t('Answer {{n}}', { n: index + 1 })}
						onChange={e => setzeAntwort(index, 'text', e.target.value)}
					/>
					<button
						type="button"
						className={styles.iconBtn}
						disabled={werte.answers.length <= MIN_ANSWERS}
						aria-label={t('Remove answer')}
						onClick={() => setze('answers', werte.answers.filter((_, i) => i !== index))}
					>
						✕
					</button>
				</div>
			))}
			{werte.answers.length < MAX_ANSWERS && (
				<Btn
					theme="minimal"
					size="small"
					onClick={() => setze('answers', [...werte.answers, { text: '', correct: false }])}
				>
					+ {t('Add answer')}
				</Btn>
			)}

			<label className={styles.field} style={{ marginTop: 12 }}>
				{t('Explanation (shown after submitting)')}
				<textarea rows={2} value={werte.explanation} onChange={e => setze('explanation', e.target.value)} />
			</label>

			<div className={styles.buttons}>
				<Btn attention={geaendert} disabled={!geaendert || speichert} loading={speichert} onClick={speichern}>
					{t('Save question')}
				</Btn>
				{frage && geaendert && (
					<Btn theme="minimal" onClick={() => setWerte(ausgang)}>
						{t('Undo changes')}
					</Btn>
				)}
				<Btn theme="minimal" onClick={loeschen}>
					{frage ? t('Delete') : t('Cancel')}
				</Btn>
			</div>
		</div>
	)
}

QuestionEditor.propTypes = {
	frage: PropTypes.object,
	nr: PropTypes.number.isRequired,
	moduleId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
	onChanged: PropTypes.func.isRequired,
	onCancel: PropTypes.func,
	markDirty: PropTypes.func.isRequired
}
